import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Users, Globe, Save } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONTENT = {
  page_key: 'join_us',
  hero_title: { fr: 'Rejoignez-nous', en: 'Join Us' },
  hero_subtitle: { fr: "Ensemble, bâtissons l'économie locale du Québec", en: "Together, let's build Quebec's local economy" },
  mission_title: { fr: 'Notre Mission', en: 'Our Mission' },
  mission_text: { fr: "PayHub Québec+ est plus qu'une plateforme de paiement...", en: "PayHub Québec+ is more than a payment platform..." },
  vision_title: { fr: 'Notre Vision', en: 'Our Vision' },
  vision_text: { fr: 'Créer un réseau interconnecté...', en: 'Create an interconnected network...' },
  values_title: { fr: 'Nos Valeurs', en: 'Our Values' },
  positions_title: { fr: 'Positions Recherchées', en: 'Open Positions' },
  positions_subtitle: { fr: 'Découvrez les rôles où vous pouvez contribuer', en: 'Discover roles where you can contribute' },
  contact_title: { fr: 'Contribuez à Notre Mission', en: 'Contribute to Our Mission' },
  contact_subtitle: { fr: 'Écrivez-nous directement par courriel', en: 'Write to us directly by email' },
  nav_title: { fr: 'Rejoignez-nous', en: 'Join Us' }
};

export default function AdminJoinUsEditor({ adminKey }) {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/join-us-content`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else {
        setContent(DEFAULT_CONTENT);
      }
    } catch (error) {
      logger.error('Error fetching join us content:', error);
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const saveContent = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/join-us-content`, {
        method: 'PUT',
        headers: { 
          'X-Admin-Key': adminKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(content)
      });
      if (response.ok) {
        toast.success(language === 'fr' ? 'Contenu sauvegardé!' : 'Content saved!');
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      logger.error('Error saving join us content:', error);
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, lang, value) => {
    setContent(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  // Bilingual field editor component
  const BilingualField = ({ field, label, type = 'input', rows = 4 }) => (
    <div className="grid md:grid-cols-2 gap-4">
      <div>
        <Label className="text-sm text-muted-foreground">{label} (FR)</Label>
        {type === 'textarea' ? (
          <Textarea 
            value={content?.[field]?.fr || ''} 
            onChange={(e) => updateField(field, 'fr', e.target.value)}
            rows={rows}
          />
        ) : (
          <Input 
            value={content?.[field]?.fr || ''} 
            onChange={(e) => updateField(field, 'fr', e.target.value)}
          />
        )}
      </div>
      <div>
        <Label className="text-sm text-muted-foreground">{label} (EN)</Label>
        {type === 'textarea' ? (
          <Textarea 
            value={content?.[field]?.en || ''} 
            onChange={(e) => updateField(field, 'en', e.target.value)}
            rows={rows}
          />
        ) : (
          <Input 
            value={content?.[field]?.en || ''} 
            onChange={(e) => updateField(field, 'en', e.target.value)}
          />
        )}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-[#003DA5]" />
          {language === 'fr' ? 'Éditeur de la page Rejoignez-nous' : 'Join Us Page Editor'}
        </CardTitle>
        <CardDescription>
          {language === 'fr' 
            ? 'Modifiez le contenu de la page Rejoignez-nous dans toutes les langues' 
            : 'Edit the Join Us page content in all languages'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : content ? (
          <>
            {/* Navigation Title */}
            <div className="p-4 bg-muted/30 rounded-xl border">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {language === 'fr' ? 'Titre du lien (Navigation)' : 'Link Title (Navigation)'}
              </h3>
              <BilingualField field="nav_title" label="Titre" />
            </div>

            {/* Hero Section */}
            <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
              <h3 className="font-semibold mb-3 text-blue-700">Hero Section</h3>
              <div className="space-y-4">
                <BilingualField field="hero_title" label="Titre" />
                <BilingualField field="hero_subtitle" label="Sous-titre" />
              </div>
            </div>

            {/* Mission Section */}
            <div className="p-4 bg-green-50/50 rounded-xl border border-green-100">
              <h3 className="font-semibold mb-3 text-green-700">
                {language === 'fr' ? 'Section Mission' : 'Mission Section'}
              </h3>
              <div className="space-y-4">
                <BilingualField field="mission_title" label="Titre" />
                <BilingualField field="mission_text" label="Texte" type="textarea" />
              </div>
            </div>

            {/* Vision Section */}
            <div className="p-4 bg-teal-50/50 rounded-xl border border-teal-100">
              <h3 className="font-semibold mb-3 text-teal-700">
                {language === 'fr' ? 'Section Vision' : 'Vision Section'}
              </h3>
              <div className="space-y-4">
                <BilingualField field="vision_title" label="Titre" />
                <BilingualField field="vision_text" label="Texte" type="textarea" />
              </div>
            </div>

            {/* Positions Section */}
            <div className="p-4 bg-purple-50/50 rounded-xl border border-purple-100">
              <h3 className="font-semibold mb-3 text-purple-700">
                {language === 'fr' ? 'Section Positions' : 'Positions Section'}
              </h3>
              <div className="space-y-4">
                <BilingualField field="positions_title" label="Titre" />
                <BilingualField field="positions_subtitle" label="Sous-titre" />
              </div>
            </div>

            {/* Contact Section */}
            <div className="p-4 bg-amber-50/50 rounded-xl border border-amber-100">
              <h3 className="font-semibold mb-3 text-amber-700">
                {language === 'fr' ? 'Section Contact' : 'Contact Section'}
              </h3>
              <div className="space-y-4">
                <BilingualField field="contact_title" label="Titre" />
                <BilingualField field="contact_subtitle" label="Sous-titre" />
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button onClick={saveContent} disabled={saving} className="gap-2">
                {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <Save className="h-4 w-4" />
                {language === 'fr' ? 'Sauvegarder' : 'Save'}
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Button onClick={fetchContent}>
              {language === 'fr' ? 'Charger le contenu' : 'Load Content'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
